import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const { language, t } = useLanguage();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPoster, setSelectedPoster] = useState(null);

  // Featured event posters
  const featuredPosters = [
    {
      id: 'poster-3',
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/3jl6jyfc_Plakat.jpg',
      title_de: 'Cinemavisione: C\'è ancora domani',
      title_it: 'Cinemavisione: C\'è ancora domani',
      date: '2026-06-06',
      location: 'Kinemathek Karlsruhe',
    },
    {
      id: 'poster-4',
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/umpwqoed_Plakat%20Apericena%20Kopie.jpg',
      title_de: 'Apericena mediterranea',
      title_it: 'Apericena mediterranea',
      date: '2026-07-03',
      location: 'Borsalino Ristorante, Karlsruhe-Durlach',
    },
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events/upcoming`);
        setUpcomingEvents(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="overflow-hidden" data-testid="events-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6" data-testid="events-title">
              {t('events_title')}
            </h1>
            <p className="text-lg text-foreground/70 max-w-2xl">
              {language === 'de'
                ? 'Entdecken Sie unsere kulturellen Veranstaltungen und werden Sie Teil der deutsch-italienischen Gemeinschaft.'
                : 'Scopri i nostri eventi culturali e diventa parte della comunità italo-tedesca.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Event Posters */}
      <section className="py-20 bg-white" data-testid="featured-posters-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12">
            {language === 'de' ? 'Aktuelle Veranstaltungen' : 'Eventi in primo piano'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredPosters.map((poster, index) => (
              <motion.div
                key={poster.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cursor-pointer group"
                onClick={() => setSelectedPoster(poster)}
                data-testid={`featured-poster-${index}`}
              >
                <div className="relative overflow-hidden luxury-shadow">
                  <img
                    src={poster.image}
                    alt={language === 'de' ? poster.title_de : poster.title_it}
                    className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="font-semibold text-sm">{language === 'de' ? poster.title_de : poster.title_it}</p>
                    <p className="text-xs text-white/80">{poster.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-20" data-testid="events-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12">
            {t('events_upcoming')}
          </h2>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-video bg-gray-200 mb-4" />
                  <div className="h-4 bg-gray-200 w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-foreground/20" />
              <p className="text-foreground/60 text-lg">{t('events_no_upcoming')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="event-card bg-white border border-black/5 overflow-hidden cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                  data-testid={`event-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={event.image_url || '/placeholder-event.svg'}
                      alt={language === 'de' ? event.title_de : event.title_it}
                      className="card-image w-full h-full object-cover transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-foreground/60 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(event.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'it-IT', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">
                      {language === 'de' ? event.title_de : event.title_it}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-foreground/60 mb-4">
                      <MapPin size={14} />
                      {event.location}
                    </div>
                    <p className="text-sm text-foreground/60 line-clamp-2 mb-4">
                      {language === 'de' ? event.description_de : event.description_it}
                    </p>
                    <span className="inline-flex items-center gap-1 text-ferrari text-sm font-semibold">
                      {t('events_details')}
                      <ChevronRight size={16} />
                    </span>
                  </div>
                  <div className="card-border h-1 bg-gray-200 transition-colors duration-300" />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedEvent(null)}
          data-testid="event-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedEvent.image_url || '/placeholder-event.svg'}
              alt={language === 'de' ? selectedEvent.title_de : selectedEvent.title_it}
              className="w-full aspect-video object-cover"
            />
            <div className="p-8">
              <div className="flex items-center gap-4 text-sm text-foreground/60 mb-4">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(selectedEvent.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'it-IT', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {selectedEvent.location}
                </span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-4">
                {language === 'de' ? selectedEvent.title_de : selectedEvent.title_it}
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                {language === 'de' ? selectedEvent.description_de : selectedEvent.description_it}
              </p>
              <button
                onClick={() => setSelectedEvent(null)}
                className="mt-8 btn-secondary"
                data-testid="close-modal-btn"
              >
                {language === 'de' ? 'Schließen' : 'Chiudi'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Poster Modal */}
      {selectedPoster && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setSelectedPoster(null)}
          data-testid="poster-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl w-full max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPoster.image}
              alt={language === 'de' ? selectedPoster.title_de : selectedPoster.title_it}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
            <button
              onClick={() => setSelectedPoster(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-foreground p-2 rounded-full transition-colors"
              data-testid="close-poster-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Events;
